/**
 * SpacesEditor.js
 * Component for editing game spaces configuration
 * Manages public, private, secret, host, diary-room, and academic location descriptions
 */

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    IconButton,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    Dialog,
    DialogContent,
    DialogTitle,
    CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ImageIcon from '@mui/icons-material/Image';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import MovieIcon from '@mui/icons-material/Movie';
import VideocamIcon from '@mui/icons-material/Videocam';
import SchoolIcon from '@mui/icons-material/School';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

// Helper function to normalize space format (handle both string and object formats)
const normalizeSpace = (space) => {
    if (typeof space === 'string') {
        return { description: space, emptyImageUrl: null, populatedImageUrl: null };
    }
    return space;
};

// Helper function to get description from space
const getSpaceDescription = (space) => {
    return typeof space === 'string' ? space : space.description;
};

// SpaceCategory component extracted to prevent re-creation on every render
const SpaceCategory = ({
    category,
    title,
    icon,
    color,
    description,
    spaces,
    newSpaceInput,
    onInputChange,
    onKeyDown,
    onAddSpace,
    onRemoveSpace,
    onViewImages,
    onRegenerateSpace,
    regeneratingSpaces
}) => {
    const categoryIcon = icon;

    return (
        <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {categoryIcon}
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        {title}
                    </Typography>
                    <Chip
                        label={`${spaces.length} location${spaces.length !== 1 ? 's' : ''}`}
                        size="small"
                        color={color}
                    />
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Typography variant="body2" color="text.secondary" paragraph>
                    {description}
                </Typography>

                {/* Existing spaces */}
                <Box sx={{ mb: 2 }}>
                    {spaces.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No {title.toLowerCase()} locations added yet
                        </Typography>
                    ) : (
                        spaces.map((space, index) => {
                            const normalizedSpace = normalizeSpace(space);
                            const hasImages = normalizedSpace.emptyImageUrl || normalizedSpace.populatedImageUrl;
                            const spaceKey = `${category}-${index}`;
                            const isRegenerating = regeneratingSpaces?.has(spaceKey);

                            return (
                                <Paper
                                    key={index}
                                    elevation={1}
                                    sx={{
                                        p: 2,
                                        mb: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        backgroundColor: isRegenerating ? 'action.hover' : 'background.default',
                                        opacity: isRegenerating ? 0.7 : 1
                                    }}
                                >
                                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                        {getSpaceDescription(space)}
                                        {isRegenerating && (
                                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'primary.main', fontStyle: 'italic' }}>
                                                (Regenerating images...)
                                            </Typography>
                                        )}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {hasImages && (
                                            <>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => onViewImages(normalizedSpace)}
                                                    title="View generated images"
                                                    disabled={isRegenerating}
                                                >
                                                    <ImageIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="secondary"
                                                    onClick={() => onRegenerateSpace(category, index)}
                                                    title="Regenerate images for this space"
                                                    disabled={isRegenerating}
                                                >
                                                    <RefreshIcon fontSize="small" className={isRegenerating ? 'spinning' : ''} />
                                                </IconButton>
                                            </>
                                        )}
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => onRemoveSpace(category, index)}
                                            disabled={isRegenerating}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Paper>
                            );
                        })
                    )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Add new space */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={`Describe a ${title.toLowerCase()} location...`}
                        value={newSpaceInput}
                        onChange={(e) => onInputChange(category, e.target.value)}
                        onKeyDown={(e) => onKeyDown(category, e)}
                        helperText="Press Enter or click Add to add this location"
                    />
                    <Button
                        variant="contained"
                        color={color}
                        startIcon={<AddIcon />}
                        onClick={() => onAddSpace(category)}
                        disabled={!newSpaceInput.trim()}
                    >
                        Add
                    </Button>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default function SpacesEditor({ spaces, onChange, gameId, apiBaseUrl }) {
    const [newSpaceInputs, setNewSpaceInputs] = useState({
        public: '',
        private: '',
        secret: '',
        host: '',
        intros: '',
        'diary-room': '',
        academic: '',
        competition: '',
        misc: ''
    });
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [selectedSpace, setSelectedSpace] = useState(null);
    const [regeneratingSpaces, setRegeneratingSpaces] = useState(new Set());

    // Candidate selection state
    const [candidateDialogOpen, setCandidateDialogOpen] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [generatingCandidates, setGeneratingCandidates] = useState(false);

    // Initialize spaces if not provided
    const currentSpaces = spaces || {
        public: [],
        private: [],
        secret: [],
        host: [],
        intros: [],
        'diary-room': [],
        academic: [],
        competition: [],
        misc: []
    };

    const handleViewImages = (space) => {
        setSelectedSpace(space);
        setImageDialogOpen(true);
    };

    const handleCloseImageDialog = () => {
        setImageDialogOpen(false);
        setSelectedSpace(null);
    };

    const handleAddSpace = async (category) => {
        const newSpace = newSpaceInputs[category].trim();
        if (!newSpace) return;

        if (!gameId || !apiBaseUrl) {
            alert('Cannot add space - missing game ID or API URL');
            return;
        }

        // Add new spaces as simple strings (backend will convert when generating images)
        const updatedSpaces = {
            ...currentSpaces,
            [category]: [...(currentSpaces[category] || []), newSpace]
        };

        // Get the index of the newly added space
        const newSpaceIndex = updatedSpaces[category].length - 1;
        const spaceKey = `${category}-${newSpaceIndex}`;

        // Clear input immediately for better UX
        setNewSpaceInputs({
            ...newSpaceInputs,
            [category]: ''
        });

        // Update local state immediately
        onChange(updatedSpaces);

        // Mark as regenerating
        setRegeneratingSpaces(prev => new Set(prev).add(spaceKey));

        try {
            // Save to backend and generate images immediately
            const response = await fetch(`${apiBaseUrl}/api/takeGameStep?updateGameSpaces=true&gameId=${gameId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    spaces: updatedSpaces,
                    generateImages: true
                })
            });

            const result = await response.json();

            if (result.success && result.spaces) {
                // Start polling for the generated images
                pollForUpdatedImages(category, newSpaceIndex, result.spaces, spaceKey);
            } else {
                setRegeneratingSpaces(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(spaceKey);
                    return newSet;
                });
                alert(`Failed to generate images: ${result.error}`);
            }
        } catch (error) {
            setRegeneratingSpaces(prev => {
                const newSet = new Set(prev);
                newSet.delete(spaceKey);
                return newSet;
            });
            console.error('Error adding space:', error);
            alert(`Error: ${error.message}`);
        }
    };

    const handleRemoveSpace = async (category, index) => {
        if (!gameId || !apiBaseUrl) {
            alert('Cannot remove space - missing game ID or API URL');
            return;
        }

        const updatedSpaces = {
            ...currentSpaces,
            [category]: currentSpaces[category].filter((_, i) => i !== index)
        };

        // Update local state immediately
        onChange(updatedSpaces);

        try {
            // Save to backend immediately (without generating images)
            const response = await fetch(`${apiBaseUrl}/api/takeGameStep?updateGameSpaces=true&gameId=${gameId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    spaces: updatedSpaces,
                    generateImages: false
                })
            });

            const result = await response.json();

            if (!result.success) {
                alert(`Failed to remove space: ${result.error}`);
                // Reload spaces to revert the change
                const reloadResponse = await fetch(`${apiBaseUrl}/api/takeGameStep?getGameSpaces=true&gameId=${gameId}`);
                const reloadResult = await reloadResponse.json();
                if (reloadResult.success && reloadResult.spaces) {
                    onChange(reloadResult.spaces);
                }
            }
        } catch (error) {
            console.error('Error removing space:', error);
            alert(`Error: ${error.message}`);
        }
    };

    const handleRegenerateSpace = async (category, index) => {
        if (!gameId || !apiBaseUrl) {
            alert('Cannot regenerate - missing game ID or API URL');
            return;
        }

        const space = currentSpaces[category][index];
        const normalizedSpace = normalizeSpace(space);

        // Open dialog and start generation
        setSelectedCategory(category);
        setSelectedIndex(index);
        setGeneratingCandidates(true);
        setCandidateDialogOpen(true);
        setCandidates([]);

        try {
            // Request 4 candidate images with AI-enhanced prompts
            const response = await fetch(`${apiBaseUrl}/api/takeGameStep?updateGameSpaces=true&gameId=${gameId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    spaces: currentSpaces,
                    generateImages: true,
                    candidateMode: true,
                    candidateCount: 4,
                    category,
                    index
                })
            });

            const result = await response.json();

            if (result.success && result.candidates) {
                setCandidates(result.candidates);
            } else {
                alert(`Failed to generate candidates: ${result.error || 'Unknown error'}`);
                setCandidateDialogOpen(false);
            }
        } catch (error) {
            console.error('Error generating candidates:', error);
            alert(`Error: ${error.message}`);
            setCandidateDialogOpen(false);
        } finally {
            setGeneratingCandidates(false);
        }
    };

    const handleGenerateMoreCandidates = async () => {
        if (!selectedCategory || selectedIndex === null) return;

        setGeneratingCandidates(true);

        try {
            const response = await fetch(`${apiBaseUrl}/api/takeGameStep?updateGameSpaces=true&gameId=${gameId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    spaces: currentSpaces,
                    generateImages: true,
                    candidateMode: true,
                    candidateCount: 4,
                    category: selectedCategory,
                    index: selectedIndex
                })
            });

            const result = await response.json();

            if (result.success && result.candidates) {
                setCandidates(result.candidates); // Replace old candidates
            } else {
                alert(`Failed to generate more candidates: ${result.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error generating more candidates:', error);
            alert(`Error: ${error.message}`);
        } finally {
            setGeneratingCandidates(false);
        }
    };

    const handleConfirmCandidate = async (candidateImageUrl, candidatePrompt) => {
        if (!selectedCategory || selectedIndex === null) return;

        try {
            const response = await fetch(`${apiBaseUrl}/api/takeGameStep?confirmSpaceCandidate=true`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    gameId,
                    category: selectedCategory,
                    index: selectedIndex,
                    selectedImageUrl: candidateImageUrl,
                    selectedPrompt: candidatePrompt
                })
            });

            const result = await response.json();

            if (result.success && result.spaces) {
                onChange(result.spaces); // Update parent state
                setCandidateDialogOpen(false);
                setCandidates([]);
                setSelectedCategory(null);
                setSelectedIndex(null);
            } else {
                alert(`Failed to confirm selection: ${result.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error confirming candidate:', error);
            alert(`Error: ${error.message}`);
        }
    };

    const pollForUpdatedImages = (category, index, initialSpaces, spaceKey) => {
        let pollCount = 0;
        const maxPolls = 60; // Poll for up to 60 seconds
        const pollInterval = 1000; // Poll every second

        const poll = setInterval(async () => {
            pollCount++;

            try {
                const response = await fetch(`${apiBaseUrl}/api/takeGameStep?getGameSpaces=true&gameId=${gameId}`);
                const result = await response.json();

                if (result.success && result.spaces) {
                    const updatedSpace = result.spaces[category]?.[index];

                    if (updatedSpace) {
                        const normalizedUpdatedSpace = normalizeSpace(updatedSpace);

                        // Check if new images are available
                        if (normalizedUpdatedSpace.emptyImageUrl || normalizedUpdatedSpace.populatedImageUrl) {
                            clearInterval(poll);
                            setRegeneratingSpaces(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(spaceKey);
                                return newSet;
                            });

                            // Update the spaces with the new images
                            onChange(result.spaces);
                            console.log('Images regenerated successfully!');
                        }
                    }
                }

                // Stop polling after max attempts
                if (pollCount >= maxPolls) {
                    clearInterval(poll);
                    setRegeneratingSpaces(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(spaceKey);
                        return newSet;
                    });
                    console.log('Polling timeout - images may still be generating');
                }
            } catch (error) {
                console.error('Error polling for images:', error);
            }
        }, pollInterval);
    };

    const handleInputChange = (category, value) => {
        setNewSpaceInputs({
            ...newSpaceInputs,
            [category]: value
        });
    };

    const handleKeyDown = (category, event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleAddSpace(category);
        }
        // Prevent space from bubbling up to parent elements
        if (event.key === ' ') {
            event.stopPropagation();
        }
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Game Spaces Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Define the physical locations where different types of conversations can take place in your game.
                These descriptions will be used to generate more immersive and contextually appropriate video scenes.
            </Typography>

            <Box sx={{ mt: 2 }}>
                <SpaceCategory
                    category="public"
                    title="Public Spaces"
                    icon={<PublicIcon color="success" />}
                    color="success"
                    description="Open areas where all players can see and hear conversations. Examples: living room, main hall, dining area, common room."
                    spaces={currentSpaces.public || []}
                    newSpaceInput={newSpaceInputs.public}
                    onInputChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onAddSpace={handleAddSpace}
                    onRemoveSpace={handleRemoveSpace}
                    onViewImages={handleViewImages}
                    onRegenerateSpace={handleRegenerateSpace}
                    regeneratingSpaces={regeneratingSpaces}
                />

                <SpaceCategory
                    category="private"
                    title="Private Spaces"
                    icon={<LockIcon color="primary" />}
                    color="primary"
                    description="Enclosed spaces where small groups can talk privately without others overhearing. Examples: bedrooms, study rooms, HOH room, private quarters."
                    spaces={currentSpaces.private || []}
                    newSpaceInput={newSpaceInputs.private}
                    onInputChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onAddSpace={handleAddSpace}
                    onRemoveSpace={handleRemoveSpace}
                    onViewImages={handleViewImages}
                    onRegenerateSpace={handleRegenerateSpace}
                    regeneratingSpaces={regeneratingSpaces}
                />

                <SpaceCategory
                    category="secret"
                    title="Secret Spaces"
                    icon={<VisibilityOffIcon color="warning" />}
                    color="warning"
                    description="Hidden or conspiratorial locations for secretive conversations. Examples: dark corners, hallways, storage areas, secluded spots."
                    spaces={currentSpaces.secret || []}
                    newSpaceInput={newSpaceInputs.secret}
                    onInputChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onAddSpace={handleAddSpace}
                    onRemoveSpace={handleRemoveSpace}
                    onViewImages={handleViewImages}
                    onRegenerateSpace={handleRegenerateSpace}
                    regeneratingSpaces={regeneratingSpaces}
                />

                <SpaceCategory
                    category="host"
                    title="Host Spaces"
                    icon={<RecordVoiceOverIcon color="secondary" />}
                    color="secondary"
                    description="Locations where the host makes announcements, conducts ceremonies, or addresses contestants. Examples: stage, podium, host room, ceremony area."
                    spaces={currentSpaces.host || []}
                    newSpaceInput={newSpaceInputs.host}
                    onInputChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onAddSpace={handleAddSpace}
                    onRemoveSpace={handleRemoveSpace}
                    onViewImages={handleViewImages}
                    onRegenerateSpace={handleRegenerateSpace}
                    regeneratingSpaces={regeneratingSpaces}
                />

                <SpaceCategory
                    category="intros"
                    title="Introduction Spaces"
                    icon={<MovieIcon sx={{ color: '#9C27B0' }} />}
                    color="secondary"
                    description="Locations for character introductions and first impressions. These spaces are used when generating full-body character images instead of intro videos. Examples: entrance hall, doorway, spotlight area, introduction stage."
                    spaces={currentSpaces.intros || []}
                    newSpaceInput={newSpaceInputs.intros}
                    onInputChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onAddSpace={handleAddSpace}
                    onRemoveSpace={handleRemoveSpace}
                    onViewImages={handleViewImages}
                    onRegenerateSpace={handleRegenerateSpace}
                    regeneratingSpaces={regeneratingSpaces}
                />

                <SpaceCategory
                    category="diary-room"
                    title="Diary Room Spaces"
                    icon={<VideocamIcon color="error" />}
                    color="error"
                    description="Private confessional spaces where players speak directly to the camera. Examples: diary room booth, confession room, interview room, recording studio."
                    spaces={currentSpaces['diary-room'] || []}
                    newSpaceInput={newSpaceInputs['diary-room']}
                    onInputChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onAddSpace={handleAddSpace}
                    onRemoveSpace={handleRemoveSpace}
                    onViewImages={handleViewImages}
                    onRegenerateSpace={handleRegenerateSpace}
                    regeneratingSpaces={regeneratingSpaces}
                />

                <SpaceCategory
                    category="academic"
                    title="Academic Spaces"
                    icon={<SchoolIcon color="info" />}
                    color="info"
                    description="Educational or study-related locations. Examples: library, study room, classroom, reading nook, research lab."
                    spaces={currentSpaces.academic || []}
                    newSpaceInput={newSpaceInputs.academic}
                    onInputChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onAddSpace={handleAddSpace}
                    onRemoveSpace={handleRemoveSpace}
                    onViewImages={handleViewImages}
                    onRegenerateSpace={handleRegenerateSpace}
                    regeneratingSpaces={regeneratingSpaces}
                />

                <SpaceCategory
                    category="competition"
                    title="Competition Spaces"
                    icon={<EmojiEventsIcon sx={{ color: '#FF6B00' }} />}
                    color="warning"
                    description="Areas designed for challenges, competitions, and physical/mental contests. Examples: obstacle course, arena, challenge room, game zone, competition field."
                    spaces={currentSpaces.competition || []}
                    newSpaceInput={newSpaceInputs.competition}
                    onInputChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onAddSpace={handleAddSpace}
                    onRemoveSpace={handleRemoveSpace}
                    onViewImages={handleViewImages}
                    onRegenerateSpace={handleRegenerateSpace}
                    regeneratingSpaces={regeneratingSpaces}
                />

                <SpaceCategory
                    category="misc"
                    title="Miscellaneous Spaces"
                    icon={<MoreHorizIcon sx={{ color: '#757575' }} />}
                    color="default"
                    description="Other locations that don't fit into the above categories. Examples: outdoor areas, special rooms, unique locations, or any custom spaces."
                    spaces={currentSpaces.misc || []}
                    newSpaceInput={newSpaceInputs.misc}
                    onInputChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onAddSpace={handleAddSpace}
                    onRemoveSpace={handleRemoveSpace}
                    onViewImages={handleViewImages}
                    onRegenerateSpace={handleRegenerateSpace}
                    regeneratingSpaces={regeneratingSpaces}
                />
            </Box>

            {/* Summary */}
            <Paper elevation={2} sx={{ p: 2, mt: 3, backgroundColor: 'info.light' }}>
                <Typography variant="body2" color="info.contrastText">
                    <strong>Total Locations:</strong> {
                        (currentSpaces.public?.length || 0) +
                        (currentSpaces.private?.length || 0) +
                        (currentSpaces.secret?.length || 0) +
                        (currentSpaces.host?.length || 0) +
                        (currentSpaces.intros?.length || 0) +
                        (currentSpaces['diary-room']?.length || 0) +
                        (currentSpaces.academic?.length || 0) +
                        (currentSpaces.competition?.length || 0) +
                        (currentSpaces.misc?.length || 0)
                    } ({currentSpaces.public?.length || 0} public, {currentSpaces.private?.length || 0} private, {currentSpaces.secret?.length || 0} secret, {currentSpaces.host?.length || 0} host, {currentSpaces.intros?.length || 0} intros, {currentSpaces['diary-room']?.length || 0} diary-room, {currentSpaces.academic?.length || 0} academic, {currentSpaces.competition?.length || 0} competition, {currentSpaces.misc?.length || 0} misc)
                </Typography>
            </Paper>

            {/* Candidate Selection Dialog */}
            <Dialog
                open={candidateDialogOpen}
                onClose={() => !generatingCandidates && setCandidateDialogOpen(false)}
                maxWidth="lg"
                fullWidth
                sx={{ zIndex: 9999 }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6">
                            Select Space Image
                        </Typography>
                        <Button
                            onClick={() => setCandidateDialogOpen(false)}
                            disabled={generatingCandidates}
                        >
                            Cancel
                        </Button>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {generatingCandidates ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
                            <CircularProgress />
                            <Typography variant="body1" color="text.secondary">
                                Generating 4 creative variations...
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                AI is creating unique interpretations of your space. This may take 30-60 seconds.
                            </Typography>
                        </Box>
                    ) : candidates.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No candidates generated yet
                        </Typography>
                    ) : (
                        <Box>
                            {/* Candidates Grid */}
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: 2,
                                mb: 3
                            }}>
                                {candidates.map((candidate, idx) => (
                                    <Paper
                                        key={idx}
                                        elevation={3}
                                        sx={{
                                            p: 2,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1
                                        }}
                                    >
                                        <img
                                            src={candidate.imageUrl}
                                            alt={`Candidate ${idx + 1}`}
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                borderRadius: '8px',
                                                display: 'block'
                                            }}
                                        />
                                        {/* Show the AI-enhanced prompt used */}
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                fontSize: '0.75rem',
                                                fontStyle: 'italic',
                                                maxHeight: '3em',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical'
                                            }}
                                        >
                                            {candidate.prompt}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            onClick={() => handleConfirmCandidate(candidate.imageUrl, candidate.prompt)}
                                        >
                                            Select This Image
                                        </Button>
                                    </Paper>
                                ))}
                            </Box>

                            {/* Generate More Button */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleGenerateMoreCandidates}
                                    disabled={generatingCandidates}
                                    startIcon={<RefreshIcon />}
                                >
                                    Generate 4 More Variations
                                </Button>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Image Dialog */}
            <Dialog
                open={imageDialogOpen}
                onClose={handleCloseImageDialog}
                maxWidth="lg"
                fullWidth
                sx={{ zIndex: 9999 }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6">
                            {selectedSpace?.description}
                        </Typography>
                        <Button onClick={handleCloseImageDialog}>Close</Button>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedSpace && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Empty Space Image */}
                            {selectedSpace.emptyImageUrl && (
                                <Box>
                                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                        Empty Space
                                    </Typography>
                                    <Paper elevation={3} sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                                        <img
                                            src={selectedSpace.emptyImageUrl}
                                            alt="Empty space"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                borderRadius: '8px',
                                                display: 'block'
                                            }}
                                        />
                                    </Paper>
                                </Box>
                            )}

                            {/* Populated Space Image */}
                            {selectedSpace.populatedImageUrl && (
                                <Box>
                                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                        With Players
                                    </Typography>
                                    <Paper elevation={3} sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                                        <img
                                            src={selectedSpace.populatedImageUrl}
                                            alt="Space with players"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                borderRadius: '8px',
                                                display: 'block'
                                            }}
                                        />
                                    </Paper>
                                </Box>
                            )}

                            {!selectedSpace.emptyImageUrl && !selectedSpace.populatedImageUrl && (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    No images have been generated for this space yet.
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
